import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, User, Store, UserPlus, ClipboardList, Package, CreditCard, BarChart3, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "profile", label: "Profile & Store", icon: Store },
  { key: "hire", label: "Hire Freelancer", icon: UserPlus },
  { key: "team", label: "Assigned Team", icon: User },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "inventory", label: "Inventory", icon: Package },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "reports", label: "Reports", icon: BarChart3 },
];

export default function ShopkeeperDashboard() {
  const [hash, setHash] = useState(window.location.hash.replace("#", "") || "overview");
  useEffect(() => {
    const on = () => setHash(window.location.hash.replace("#", "") || "overview");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const titles = { overview: "Overview", profile: "Profile & Store", hire: "Hire Freelancer", team: "Assigned Team", tasks: "Tasks", inventory: "Inventory", payments: "Payments", reports: "Reports" };
  return (
    <DashboardLayout nav={NAV} title={titles[hash]}>
      {hash === "overview" && <Overview />}
      {hash === "profile" && <Profile />}
      {hash === "hire" && <Hire />}
      {hash === "team" && <Team />}
      {hash === "tasks" && <TasksView />}
      {hash === "inventory" && <Inventory />}
      {hash === "payments" && <Payments />}
      {hash === "reports" && <Reports />}
    </DashboardLayout>
  );
}

function KPI({ label, value, tint = "blue" }) {
  const color = tint === "orange" ? "#FF6A00" : "#155EEF";
  return (
    <div className="card-soft p-6">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 font-heading font-bold text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}

function Overview() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  useEffect(() => { (async () => {
    const [a, t, p] = await Promise.all([api.get("/shopkeeper/assignments"), api.get("/tasks"), api.get("/payments")]);
    setAssignments(a.data); setTasks(t.data); setPayments(p.data);
  })(); }, []);
  const open = tasks.filter(t => t.status !== "done").length;
  const done = tasks.filter(t => t.status === "done").length;
  const spent = payments.reduce((s, p) => s + p.amount, 0);
  return (
    <div className="space-y-6">
      <div className="card-soft p-8" style={{ background: "linear-gradient(135deg,#155EEF 0%,#0B3FB8 100%)" }}>
        <div className="text-white/70 text-sm">Welcome back</div>
        <h2 className="mt-1 font-heading font-bold text-3xl text-white">{user?.name}</h2>
        <p className="mt-2 text-white/80 max-w-2xl">Manage your online store operations, tasks and freelancers — all in one place.</p>
        <div className="mt-6 flex gap-3">
          <Button onClick={() => (window.location.hash = "hire")} data-testid="ov-hire" className="btn-orange hover:bg-[#FF6A00]">Find freelancers</Button>
          <Button onClick={() => (window.location.hash = "tasks")} variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="ov-tasks">Create task</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Team members" value={assignments.length} />
        <KPI label="Open tasks" value={open} tint="orange" />
        <KPI label="Completed" value={done} />
        <KPI label="Total paid" value={`₹${spent.toLocaleString()}`} tint="orange" />
      </div>
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "", phone: user?.phone || "", city: user?.city || "",
    store_name: user?.store_name || "", marketplaces: (user?.marketplaces || []).join(", "),
  });
  const save = async () => {
    const payload = { ...f, marketplaces: f.marketplaces.split(",").map(s => s.trim()).filter(Boolean) };
    const { data } = await api.patch("/auth/me", payload);
    setUser(data); localStorage.setItem("sellexr_user", JSON.stringify(data));
    toast.success("Profile updated");
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-soft p-8 space-y-4">
        <h3 className="font-heading font-bold text-lg">Store details</h3>
        {[
          ["Store name", "store_name"], ["Owner name", "name"], ["Phone", "phone"], ["City", "city"],
        ].map(([lbl, k]) => (
          <div key={k}>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{lbl}</label>
            <Input data-testid={`sk-profile-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" />
          </div>
        ))}
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Marketplaces (comma separated)</label>
          <Input data-testid="sk-profile-marketplaces" value={f.marketplaces} onChange={(e) => setF({ ...f, marketplaces: e.target.value })} placeholder="Amazon, Flipkart, Meesho" className="mt-1.5" />
        </div>
        <Button onClick={save} data-testid="sk-profile-save" className="btn-orange hover:bg-[#FF6A00]">Save changes</Button>
      </div>
      <div className="card-soft p-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-2xl" style={{ background: user?.avatar_color || "#155EEF" }}>{user?.name?.[0]}</div>
        <div className="mt-4 font-heading font-bold text-lg">{user?.name}</div>
        <div className="text-sm text-slate-500">{user?.email}</div>
        <Badge className="mt-3 bg-[#155EEF]/10 text-[#155EEF] hover:bg-[#155EEF]/10">{user?.role}</Badge>
      </div>
    </div>
  );
}

function Hire() {
  const [freelancers, setFreelancers] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/freelancers").then(r => setFreelancers(r.data)); }, []);
  const runAI = async () => {
    setLoading(true);
    try { const { data } = await api.post("/ai/match", { prompt }); setMatches(data); toast.success("AI matches ready"); }
    catch { toast.error("AI matching failed"); } finally { setLoading(false); }
  };
  const hire = async (id) => {
    try { await api.post("/shopkeeper/hire", { freelancer_id: id }); toast.success("Hired! Check your Assigned Team."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="card-soft p-6" style={{ background: "linear-gradient(135deg,#FFF7ED 0%,#FFEDD5 100%)", borderColor: "#FED7AA" }}>
        <div className="flex items-center gap-2 text-[#FF6A00]"><Sparkles size={20} /><span className="font-heading font-bold">AI Client Matching</span></div>
        <p className="mt-2 text-sm text-slate-700">Describe what you need help with. Claude Sonnet 5 will surface the top 3 freelancers.</p>
        <Textarea data-testid="ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. I sell handmade home decor on Amazon India. Need help with listings, A+ content and PPC." className="mt-4 bg-white" />
        <Button onClick={runAI} disabled={loading} data-testid="ai-run" className="mt-4 btn-orange hover:bg-[#FF6A00]">{loading ? "Matching..." : "Find best matches"}</Button>
      </div>
      {matches && (
        <div className="card-soft p-6">
          <div className="font-heading font-bold mb-1">AI Recommendations</div>
          <p className="text-sm text-slate-600 mb-4">{matches.reasoning}</p>
          <div className="grid md:grid-cols-3 gap-4">
            {matches.matches.map((m) => (
              <div key={m.id} className="border border-[#FF6A00]/30 bg-[#FF6A00]/5 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: m.freelancer?.avatar_color || "#155EEF" }}>{m.freelancer?.name?.[0]}</div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{m.freelancer?.name}</div>
                    <div className="text-xs text-slate-500">Score {m.score}/100</div>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-3">{m.reason}</p>
                <Button onClick={() => hire(m.id)} data-testid={`hire-ai-${m.id}`} className="btn-orange hover:bg-[#FF6A00] mt-3 w-full">Hire</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="font-heading font-bold text-lg mb-4">All certified freelancers</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {freelancers.map((f) => (
            <div key={f.id} className="card-soft p-6" data-testid={`fl-card-${f.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: f.avatar_color || "#155EEF" }}>{f.name?.[0]}</div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{f.name}</div>
                  <div className="text-xs text-slate-500">{f.city || "Remote"} · ★ {f.rating || 0}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(f.skills || []).slice(0, 3).map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
              </div>
              <p className="text-xs text-slate-600 mt-3 line-clamp-2">{f.bio}</p>
              <Button onClick={() => hire(f.id)} data-testid={`hire-${f.id}`} variant="outline" className="mt-4 w-full border-[#155EEF] text-[#155EEF] hover:bg-[#155EEF] hover:text-white">Hire</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Team() {
  const [list, setList] = useState([]);
  const [rating, setRating] = useState({});
  const load = () => api.get("/shopkeeper/assignments").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const end = async (id) => { await api.delete(`/shopkeeper/assignments/${id}`); toast.success("Ended"); load(); };
  const rate = async (fid) => {
    const r = rating[fid];
    if (!r?.rating) return;
    await api.post("/ratings", { freelancer_id: fid, rating: parseFloat(r.rating), review: r.review });
    toast.success("Rating submitted");
    setRating({ ...rating, [fid]: {} });
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {list.length === 0 && <div className="text-slate-500 col-span-2">No team yet. Hire a freelancer to start.</div>}
      {list.map(a => (
        <div key={a.id} className="card-soft p-6" data-testid={`team-${a.id}`}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: a.freelancer?.avatar_color || "#155EEF" }}>{a.freelancer?.name?.[0]}</div>
            <div className="min-w-0">
              <div className="font-semibold truncate">{a.freelancer?.name}</div>
              <div className="text-xs text-slate-500">{a.freelancer?.email}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-slate-500">Rating:</span> <b>★ {a.freelancer?.rating || 0}</b></div>
            <div><span className="text-slate-500">Skills:</span> {(a.freelancer?.skills || []).length}</div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Input placeholder="★ 1-5" type="number" min={1} max={5} step={0.5} value={rating[a.freelancer_id]?.rating || ""} onChange={e => setRating({ ...rating, [a.freelancer_id]: { ...rating[a.freelancer_id], rating: e.target.value } })} data-testid={`rate-num-${a.freelancer_id}`} />
            <Input placeholder="Review" value={rating[a.freelancer_id]?.review || ""} onChange={e => setRating({ ...rating, [a.freelancer_id]: { ...rating[a.freelancer_id], review: e.target.value } })} className="col-span-2" data-testid={`rate-txt-${a.freelancer_id}`} />
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => rate(a.freelancer_id)} className="btn-orange hover:bg-[#FF6A00]" data-testid={`rate-submit-${a.freelancer_id}`}>Rate</Button>
            <Button onClick={() => end(a.id)} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`end-${a.id}`}>End</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TasksView() {
  const [tasks, setTasks] = useState([]);
  const [assign, setAssign] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", freelancer_id: "" });
  const load = () => { api.get("/tasks").then(r => setTasks(r.data)); api.get("/shopkeeper/assignments").then(r => setAssign(r.data)); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title) return;
    const payload = { ...form, freelancer_id: form.freelancer_id || null };
    await api.post("/tasks", payload);
    toast.success("Task created"); setForm({ title: "", description: "", priority: "medium", freelancer_id: "" }); load();
  };
  const update = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };
  const cols = [["todo", "To do"], ["in_progress", "In progress"], ["done", "Done"]];
  return (
    <div className="space-y-6">
      <div className="card-soft p-6">
        <div className="grid md:grid-cols-4 gap-3">
          <Input placeholder="Task title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
          <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
            <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
          </Select>
          <Select value={form.freelancer_id} onValueChange={v => setForm({ ...form, freelancer_id: v })}>
            <SelectTrigger data-testid="task-assign"><SelectValue placeholder="Assign to..." /></SelectTrigger>
            <SelectContent>{assign.map(a => <SelectItem key={a.freelancer_id} value={a.freelancer_id}>{a.freelancer?.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={create} className="btn-orange hover:bg-[#FF6A00]" data-testid="task-create">Create task</Button>
        </div>
        <Textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-3" data-testid="task-desc" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {cols.map(([key, label]) => (
          <div key={key} className="card-soft p-4">
            <div className="font-heading font-bold mb-3 flex items-center justify-between">
              {label} <Badge variant="secondary">{tasks.filter(t => t.status === key).length}</Badge>
            </div>
            <div className="space-y-2">
              {tasks.filter(t => t.status === key).map(t => (
                <div key={t.id} className="border border-slate-200 rounded-lg p-3" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm">{t.title}</div>
                    <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600" data-testid={`task-del-${t.id}`}><Trash2 size={14} /></button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>
                  <div className="mt-2 flex gap-1">
                    {cols.filter(([k]) => k !== key).map(([k, l]) => (
                      <Button key={k} onClick={() => update(t.id, k)} variant="outline" size="sm" className="text-xs h-7" data-testid={`task-move-${t.id}-${k}`}>{l}</Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Inventory() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", sku: "", stock: 0, price: 0, marketplace: "Amazon" });
  const load = () => api.get("/inventory").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name || !f.sku) return;
    await api.post("/inventory", { ...f, stock: +f.stock, price: +f.price });
    toast.success("Item added"); setF({ name: "", sku: "", stock: 0, price: 0, marketplace: "Amazon" }); load();
  };
  const del = async (id) => { await api.delete(`/inventory/${id}`); load(); };
  return (
    <div className="space-y-6">
      <div className="card-soft p-6 grid md:grid-cols-6 gap-3">
        <Input placeholder="Product name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="inv-name" className="md:col-span-2" />
        <Input placeholder="SKU" value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} data-testid="inv-sku" />
        <Input placeholder="Stock" type="number" value={f.stock} onChange={e => setF({ ...f, stock: e.target.value })} data-testid="inv-stock" />
        <Input placeholder="Price ₹" type="number" value={f.price} onChange={e => setF({ ...f, price: e.target.value })} data-testid="inv-price" />
        <div className="flex gap-2">
          <Select value={f.marketplace} onValueChange={v => setF({ ...f, marketplace: v })}>
            <SelectTrigger data-testid="inv-mp"><SelectValue /></SelectTrigger>
            <SelectContent>{["Amazon", "Flipkart", "Meesho", "JioMart", "Ajio"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={add} className="btn-orange hover:bg-[#FF6A00]" data-testid="inv-add">Add</Button>
        </div>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>{["Product", "SKU", "Marketplace", "Stock", "Price", ""].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-b border-slate-100" data-testid={`inv-row-${i.id}`}>
                <td className="p-4 font-semibold">{i.name}</td>
                <td className="p-4 text-slate-500">{i.sku}</td>
                <td className="p-4"><Badge variant="secondary">{i.marketplace}</Badge></td>
                <td className="p-4"><span className={i.stock < 10 ? "text-[#FF6A00] font-semibold" : ""}>{i.stock}</span></td>
                <td className="p-4">₹{i.price}</td>
                <td className="p-4 text-right"><button onClick={() => del(i.id)} data-testid={`inv-del-${i.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No inventory yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Payments() {
  const [list, setList] = useState([]);
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ freelancer_id: "", amount: "", note: "" });
  const load = () => { api.get("/payments").then(r => setList(r.data)); api.get("/shopkeeper/assignments").then(r => setTeam(r.data)); };
  useEffect(() => { load(); }, []);
  const pay = async () => {
    if (!f.freelancer_id || !f.amount) return;
    await api.post("/payments", { ...f, amount: parseFloat(f.amount) });
    toast.success("Payment recorded"); setF({ freelancer_id: "", amount: "", note: "" }); load();
  };
  return (
    <div className="space-y-6">
      <div className="card-soft p-6 grid md:grid-cols-4 gap-3">
        <Select value={f.freelancer_id} onValueChange={v => setF({ ...f, freelancer_id: v })}>
          <SelectTrigger data-testid="pay-fl"><SelectValue placeholder="Freelancer" /></SelectTrigger>
          <SelectContent>{team.map(a => <SelectItem key={a.freelancer_id} value={a.freelancer_id}>{a.freelancer?.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Amount ₹" type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="pay-amt" />
        <Input placeholder="Note" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid="pay-note" />
        <Button onClick={pay} className="btn-orange hover:bg-[#FF6A00]" data-testid="pay-submit">Record payment</Button>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Date", "Freelancer", "Amount", "Note", "Status"].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {list.map(p => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`pay-${p.id}`}>
                <td className="p-4 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4">{team.find(t => t.freelancer_id === p.freelancer_id)?.freelancer?.name || "—"}</td>
                <td className="p-4 font-semibold text-[#FF6A00]">₹{p.amount}</td>
                <td className="p-4 text-slate-500">{p.note || "—"}</td>
                <td className="p-4"><Badge className="bg-green-100 text-green-700 hover:bg-green-100">{p.status}</Badge></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No payments yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reports() {
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  useEffect(() => { api.get("/tasks").then(r => setTasks(r.data)); api.get("/payments").then(r => setPayments(r.data)); }, []);
  const byMonth = {};
  payments.forEach(p => {
    const k = new Date(p.created_at).toLocaleString("en", { month: "short" });
    byMonth[k] = (byMonth[k] || 0) + p.amount;
  });
  const chart = Object.entries(byMonth).map(([month, amount]) => ({ month, amount }));
  if (chart.length === 0) chart.push(...["Jan", "Feb", "Mar"].map(m => ({ month: m, amount: 0 })));
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-soft p-6">
        <div className="font-heading font-bold mb-4">Payments by month</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chart}>
            <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip />
            <Bar dataKey="amount" fill="#155EEF" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card-soft p-6 space-y-3">
        <div className="font-heading font-bold">Snapshot</div>
        <div className="flex justify-between"><span className="text-slate-500">Tasks total</span><b>{tasks.length}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">Completed</span><b>{tasks.filter(t => t.status === "done").length}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">In progress</span><b>{tasks.filter(t => t.status === "in_progress").length}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">Total paid</span><b className="text-[#FF6A00]">₹{payments.reduce((s, p) => s + p.amount, 0)}</b></div>
      </div>
    </div>
  );
}
